"use client";

import { c as _c } from "react/compiler-runtime";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import { PanelLeftIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { SIDEBAR_STATE_COOKIE } from "@/lib/storage-keys";
import { cn } from "@/lib/utils";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";
type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};
const SidebarContext = React.createContext<SidebarContextProps | null>(null);
function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}
function SidebarProvider(t0) {
  const $ = _c(36);
  let children;
  let className;
  let openProp;
  let props;
  let setOpenProp;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      defaultOpen: t1,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = openProp;
    $[4] = props;
    $[5] = setOpenProp;
    $[6] = style;
    $[7] = t1;
  } else {
    children = $[1];
    className = $[2];
    openProp = $[3];
    props = $[4];
    setOpenProp = $[5];
    style = $[6];
    t1 = $[7];
  }
  const defaultOpen = t1 === undefined ? true : t1;
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const pathname = usePathname();
  const [previousPathname, setPreviousPathname] = React.useState(pathname);
  if (pathname !== previousPathname) {
    setPreviousPathname(pathname);
    setOpenMobile(false);
  }
  const [_open, _setOpen] = React.useState(openProp ?? defaultOpen);
  const open = openProp ?? _open;
  let t2;
  if ($[8] !== open || $[9] !== setOpenProp) {
    t2 = value => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_STATE_COOKIE}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}; SameSite=Lax`;
    };
    $[8] = open;
    $[9] = setOpenProp;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const setOpen = t2;
  let t3;
  if ($[11] !== isMobile || $[12] !== setOpen) {
    t3 = () => isMobile ? setOpenMobile(_temp) : setOpen(_temp2);
    $[11] = isMobile;
    $[12] = setOpen;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const toggleSidebar = t3;
  let t4;
  let t5;
  if ($[14] !== toggleSidebar) {
    t4 = () => {
      const handleKeyDown = event => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t5 = [toggleSidebar];
    $[14] = toggleSidebar;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  React.useEffect(t4, t5);
  const state = open ? "expanded" : "collapsed";
  let t6;
  if ($[17] !== isMobile || $[18] !== open || $[19] !== openMobile || $[20] !== setOpen || $[21] !== state || $[22] !== toggleSidebar) {
    t6 = {
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar
    };
    $[17] = isMobile;
    $[18] = open;
    $[19] = openMobile;
    $[20] = setOpen;
    $[21] = state;
    $[22] = toggleSidebar;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const contextValue = t6;
  let t7;
  if ($[24] !== style) {
    t7 = {
      "--sidebar-width": SIDEBAR_WIDTH,
      "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
      ...style
    };
    $[24] = style;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const t8 = t7 as React.CSSProperties;
  let t9;
  if ($[26] !== className) {
    t9 = cn("group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full", className);
    $[26] = className;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== children || $[29] !== props || $[30] !== t8 || $[31] !== t9) {
    t10 = <TooltipProvider delay={0}><div data-slot="sidebar-wrapper" style={t8} className={t9} {...props}>{children}</div></TooltipProvider>;
    $[28] = children;
    $[29] = props;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== contextValue || $[34] !== t10) {
    t11 = <SidebarContext.Provider value={contextValue}>{t10}</SidebarContext.Provider>;
    $[33] = contextValue;
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function _temp2(open_1) {
  return !open_1;
}
function _temp(open_0) {
  return !open_0;
}
function Sidebar(t0) {
  const $ = _c(48);
  let children;
  let className;
  let dir;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      side: t1,
      variant: t2,
      collapsible: t3,
      className,
      children,
      dir,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = dir;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    children = $[1];
    className = $[2];
    dir = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  const side = t1 === undefined ? "left" : t1;
  const variant = t2 === undefined ? "sidebar" : t2;
  const collapsible = t3 === undefined ? "offcanvas" : t3;
  const {
    isMobile,
    state,
    openMobile,
    setOpenMobile
  } = useSidebar();
  if (collapsible === "none") {
    let t4;
    if ($[8] !== className) {
      t4 = cn("bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col", className);
      $[8] = className;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== children || $[11] !== props || $[12] !== t4) {
      t5 = <div data-slot="sidebar" className={t4} {...props}>{children}</div>;
      $[10] = children;
      $[11] = props;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (isMobile) {
    let t4;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        "--sidebar-width": SIDEBAR_WIDTH_MOBILE
      };
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SheetHeader className="sr-only"><SheetTitle>Barra lateral</SheetTitle><SheetDescription>Navegação do aplicativo.</SheetDescription></SheetHeader>;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== children) {
      t6 = <div className="flex size-full flex-col">{children}</div>;
      $[16] = children;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== dir || $[19] !== side || $[20] !== t6) {
      t7 = <SheetContent dir={dir} data-sidebar="sidebar" data-slot="sidebar" data-mobile="true" className="bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden" style={t4 as React.CSSProperties} side={side}>{t5}{t6}</SheetContent>;
      $[18] = dir;
      $[19] = side;
      $[20] = t6;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] !== openMobile || $[23] !== props || $[24] !== setOpenMobile || $[25] !== t7) {
      t8 = <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>{t7}</Sheet>;
      $[22] = openMobile;
      $[23] = props;
      $[24] = setOpenMobile;
      $[25] = t7;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    return t8;
  }
  const t4 = state === "collapsed" ? collapsible : "";
  const t5 = variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)";
  let t6;
  if ($[27] !== t5) {
    t6 = cn("relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear", "group-data-[collapsible=offcanvas]:w-0", "group-data-[side=right]:rotate-180", t5);
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t6) {
    t7 = <div data-slot="sidebar-gap" className={t6} />;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const t8 = variant === "floating" || variant === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l";
  let t9;
  if ($[31] !== className || $[32] !== t8) {
    t9 = cn("fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear data-[side=left]:left-0 data-[side=left]:group-data-[collapsible=offcanvas]:-left-(--sidebar-width) data-[side=right]:right-0 data-[side=right]:group-data-[collapsible=offcanvas]:-right-(--sidebar-width) md:flex", t8, className);
    $[31] = className;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== children) {
    t10 = <div data-sidebar="sidebar" data-slot="sidebar-inner" className="bg-sidebar group-data-[variant=floating]:ring-sidebar-border flex size-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1">{children}</div>;
    $[34] = children;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== props || $[37] !== side || $[38] !== t10 || $[39] !== t9) {
    t11 = <div data-slot="sidebar-container" data-side={side} className={t9} {...props}>{t10}</div>;
    $[36] = props;
    $[37] = side;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== side || $[42] !== state || $[43] !== t11 || $[44] !== t4 || $[45] !== t7 || $[46] !== variant) {
    t12 = <div className="group peer text-sidebar-foreground hidden md:block" data-state={state} data-collapsible={t4} data-variant={variant} data-side={side} data-slot="sidebar">{t7}{t11}</div>;
    $[41] = side;
    $[42] = state;
    $[43] = t11;
    $[44] = t4;
    $[45] = t7;
    $[46] = variant;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  return t12;
}
function SidebarTrigger(t0) {
  const $ = _c(15);
  let className;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onClick;
    $[3] = props;
  } else {
    className = $[1];
    onClick = $[2];
    props = $[3];
  }
  const {
    toggleSidebar
  } = useSidebar();
  let t1;
  if ($[4] !== className) {
    t1 = cn(className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== onClick || $[7] !== toggleSidebar) {
    t2 = event => {
      onClick?.(event);
      toggleSidebar();
    };
    $[6] = onClick;
    $[7] = toggleSidebar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PanelLeftIcon />;
    t4 = <span className="sr-only">Alternar barra lateral</span>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props || $[12] !== t1 || $[13] !== t2) {
    t5 = <Button data-sidebar="trigger" data-slot="sidebar-trigger" variant="ghost" size="icon-sm" className={t1} onClick={t2} {...props}>{t3}{t4}</Button>;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function SidebarRail(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    toggleSidebar
  } = useSidebar();
  let t1;
  if ($[3] !== className) {
    t1 = cn("hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:inset-s-1/2 after:w-[2px] sm:flex ltr:-translate-x-1/2 rtl:-translate-x-1/2", "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize", "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize", "hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full", "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2", "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1 || $[7] !== toggleSidebar) {
    t2 = <button data-sidebar="rail" data-slot="sidebar-rail" aria-label="Alternar barra lateral" tabIndex={-1} onClick={toggleSidebar} title="Alternar barra lateral" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = toggleSidebar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function SidebarInset(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-background relative flex w-full flex-1 flex-col md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <main data-slot="sidebar-inset" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarInput(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-input bg-muted/20 dark:bg-muted/30 h-8 w-full", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Input data-slot="sidebar-input" data-sidebar="input" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-header" data-sidebar="header" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarFooter(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-footer" data-sidebar="footer" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-sidebar-border mx-2 w-auto", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Separator data-slot="sidebar-separator" data-sidebar="separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("no-scrollbar flex min-h-0 flex-1 flex-col gap-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-content" data-sidebar="content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex w-full min-w-0 flex-col px-2 py-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-group" data-sidebar="group" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroupLabel(t0) {
  const $ = _c(10);
  let render;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      className,
      render: t4,
      ...props
    } = t0;
    render = t4;
    t3 = useRender;
    t1 = "div";
    t2 = mergeProps({
      className: cn("text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs outline-hidden transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", className)
    }, props);
    $[0] = t0;
    $[1] = render;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    render = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      slot: "sidebar-group-label",
      sidebar: "group-label"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== render || $[7] !== t1 || $[8] !== t2) {
    t5 = {
      defaultTagName: t1,
      props: t2,
      render,
      state: t4
    };
    $[6] = render;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t3(t5);
}
function SidebarGroupAction(t0) {
  const $ = _c(10);
  let render;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      className,
      render: t4,
      ...props
    } = t0;
    render = t4;
    t3 = useRender;
    t1 = "button";
    t2 = mergeProps({
      className: cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0", className)
    }, props);
    $[0] = t0;
    $[1] = render;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    render = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      slot: "sidebar-group-action",
      sidebar: "group-action"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== render || $[7] !== t1 || $[8] !== t2) {
    t5 = {
      defaultTagName: t1,
      props: t2,
      render,
      state: t4
    };
    $[6] = render;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t3(t5);
}
function SidebarGroupContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full text-xs", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-group-content" data-sidebar="group-content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenu(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex w-full min-w-0 flex-col gap-px", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul data-slot="sidebar-menu" data-sidebar="menu" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <li data-slot="sidebar-menu-item" data-sidebar="menu-item" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
const sidebarMenuButtonVariants = cva("peer/menu-button group/menu-button ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground flex w-full items-center gap-2 overflow-hidden rounded-[calc(var(--radius-sm)+2px)] p-2 text-left text-xs outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-active:font-medium [&_svg]:size-4 [&_svg]:shrink-0 [&>span:last-child]:truncate", {
  variants: {
    variant: {
      default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      outline: "bg-background hover:bg-sidebar-accent hover:text-sidebar-accent-foreground shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]"
    },
    size: {
      default: "h-8 text-xs",
      sm: "h-7 text-xs",
      lg: "h-12 text-xs group-data-[collapsible=icon]:p-0!"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function SidebarMenuButton(t0) {
  const $ = _c(18);
  let {
    render,
    isActive: t1,
    variant: t2,
    size: t3,
    tooltip,
    className,
    ...props
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "default" : t2;
  const size = t3 === undefined ? "default" : t3;
  const {
    isMobile,
    state
  } = useSidebar();
  const t4 = "button";
  const t5 = mergeProps({
    className: cn(sidebarMenuButtonVariants({
      variant,
      size
    }), className)
  }, props);
  let t6;
  if ($[0] !== render || $[1] !== tooltip) {
    t6 = !tooltip ? render : <TooltipTrigger render={render} />;
    $[0] = render;
    $[1] = tooltip;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== isActive || $[4] !== size) {
    t7 = {
      slot: "sidebar-menu-button",
      sidebar: "menu-button",
      size,
      active: isActive
    };
    $[3] = isActive;
    $[4] = size;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t5 || $[7] !== t6 || $[8] !== t7) {
    t8 = {
      defaultTagName: t4,
      props: t5,
      render: t6,
      state: t7
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const button = useRender(t8);
  if (!tooltip) {
    return button;
  }
  if (typeof tooltip === "string") {
    let t9;
    if ($[10] !== tooltip) {
      t9 = {
        children: tooltip
      };
      $[10] = tooltip;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    tooltip = t9;
  }
  const t9 = state !== "collapsed" || isMobile;
  let t10;
  if ($[12] !== t9 || $[13] !== tooltip) {
    t10 = <TooltipContent side="right" align="center" hidden={t9} {...tooltip} />;
    $[12] = t9;
    $[13] = tooltip;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== button || $[16] !== t10) {
    t11 = <Tooltip>{button}{t10}</Tooltip>;
    $[15] = button;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}
function SidebarMenuAction(t0) {
  const $ = _c(10);
  let render;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      className,
      render: t4,
      showOnHover: t5,
      ...props
    } = t0;
    render = t4;
    const showOnHover = t5 === undefined ? false : t5;
    t3 = useRender;
    t1 = "button";
    t2 = mergeProps({
      className: cn("text-sidebar-foreground ring-sidebar-ring peer-hover/menu-button:text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-[calc(var(--radius-sm)-2px)] p-0 outline-hidden transition-transform group-data-[collapsible=icon]:hidden peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 after:absolute after:-inset-2 focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0", showOnHover && "peer-data-active/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 aria-expanded:opacity-100 md:opacity-0", className)
    }, props);
    $[0] = t0;
    $[1] = render;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    render = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      slot: "sidebar-menu-action",
      sidebar: "menu-action"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== render || $[7] !== t1 || $[8] !== t2) {
    t5 = {
      defaultTagName: t1,
      props: t2,
      render,
      state: t4
    };
    $[6] = render;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t3(t5);
}
function SidebarMenuBadge(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sidebar-foreground peer-hover/menu-button:text-sidebar-accent-foreground peer-data-active/menu-button:text-sidebar-accent-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-[calc(var(--radius-sm)-2px)] px-1 text-xs font-medium tabular-nums select-none group-data-[collapsible=icon]:hidden peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="sidebar-menu-badge" data-sidebar="menu-badge" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSkeleton(t0) {
  const $ = _c(17);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      showIcon: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const showIcon = t1 === undefined ? false : t1;
  const [width] = React.useState(_temp3);
  let t2;
  if ($[4] !== className) {
    t2 = cn("flex h-8 items-center gap-2 rounded-md px-2", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== showIcon) {
    t3 = showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />;
    $[6] = showIcon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== width) {
    t4 = {
      "--skeleton-width": width
    };
    $[8] = width;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = t4 as React.CSSProperties;
  let t6;
  if ($[10] !== t5) {
    t6 = <Skeleton className="h-4 max-w-(--skeleton-width) flex-1" data-sidebar="menu-skeleton-text" style={t5} />;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== props || $[13] !== t2 || $[14] !== t3 || $[15] !== t6) {
    t7 = <div data-slot="sidebar-menu-skeleton" data-sidebar="menu-skeleton" className={t2} {...props}>{t3}{t6}</div>;
    $[12] = props;
    $[13] = t2;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function _temp3() {
  return `${Math.floor(Math.random() * 40) + 50}%`;
}
function SidebarMenuSub(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2.5 py-0.5 group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul data-slot="sidebar-menu-sub" data-sidebar="menu-sub" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSubItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-sub-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <li data-slot="sidebar-menu-sub-item" data-sidebar="menu-sub-item" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSubButton(t0) {
  const $ = _c(15);
  let isActive;
  let render;
  let size;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      render: t4,
      size: t5,
      isActive: t6,
      className,
      ...props
    } = t0;
    render = t4;
    size = t5 === undefined ? "md" : t5;
    isActive = t6 === undefined ? false : t6;
    t3 = useRender;
    t1 = "a";
    t2 = mergeProps({
      className: cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 outline-hidden group-data-[collapsible=icon]:hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[size=md]:text-xs data-[size=sm]:text-xs [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0", className)
    }, props);
    $[0] = t0;
    $[1] = isActive;
    $[2] = render;
    $[3] = size;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    isActive = $[1];
    render = $[2];
    size = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isActive || $[8] !== size) {
    t4 = {
      slot: "sidebar-menu-sub-button",
      sidebar: "menu-sub-button",
      size,
      active: isActive
    };
    $[7] = isActive;
    $[8] = size;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== render || $[11] !== t1 || $[12] !== t2 || $[13] !== t4) {
    t5 = {
      defaultTagName: t1,
      props: t2,
      render,
      state: t4
    };
    $[10] = render;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t3(t5);
}
export { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger, useSidebar };